/**
 * Video File Validation Utilities
 * Validates file size, duration, and type before upload
 */

import { getVideoDuration } from './video-thumbnail';

// Constants matching mobile implementation
const MAX_FILE_SIZE_BYTES = 200 * 1024 * 1024; // 200 MB
const MAX_DURATION_SECONDS = 120; // 2 minutes
const ALLOWED_MIME_TYPES = ['video/mp4', 'video/quicktime', 'video/x-msvideo'];

export interface ValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Validate video file before upload
 * Checks file type, size, and duration
 *
 * @param file - Video file to validate
 * @returns Promise with validation result
 */
export const validateVideoFile = async (file: File): Promise<ValidationResult> => {
  console.log('[VideoValidation] Validating video file...', {
    fileName: file.name,
    fileSize: file.size,
    fileType: file.type,
  });

  // Check file type
  if (!ALLOWED_MIME_TYPES.includes(file.type)) {
    console.warn('[VideoValidation] Invalid MIME type', {
      fileName: file.name,
      fileType: file.type,
      allowed: ALLOWED_MIME_TYPES,
    });
    return {
      valid: false,
      error: 'Invalid file type. Please upload MP4, MOV, or AVI video files.',
    };
  }

  // Check file size
  if (file.size > MAX_FILE_SIZE_BYTES) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
    const maxSizeMB = MAX_FILE_SIZE_BYTES / (1024 * 1024);
    console.warn('[VideoValidation] File too large', {
      fileName: file.name,
      fileSizeMB: sizeMB,
      maxSizeMB,
    });
    return {
      valid: false,
      error: `File too large (${sizeMB} MB). Maximum size is ${maxSizeMB} MB.`,
    };
  }

  // Check duration
  try {
    const duration = await getVideoDuration(file);
    if (duration > MAX_DURATION_SECONDS) {
      const minutes = Math.floor(duration / 60);
      const seconds = duration % 60;
      console.warn('[VideoValidation] Video too long', {
        fileName: file.name,
        durationSeconds: duration,
        maxDurationSeconds: MAX_DURATION_SECONDS,
      });
      return {
        valid: false,
        error: `Video too long (${minutes}:${seconds.toString().padStart(2, '0')}). Maximum duration is 2 minutes.`,
      };
    }
  } catch (error) {
    console.error('[VideoValidation] Failed to read video metadata', {
      error,
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
    });
    return {
      valid: false,
      error: 'Failed to read video metadata. The video file may be corrupted or in an unsupported format.',
    };
  }

  console.log('[VideoValidation] Validation passed', {
    fileName: file.name,
  });

  return { valid: true };
};

/**
 * Get file extension from filename
 *
 * @param file - File to extract extension from
 * @returns File extension with leading dot (e.g., ".mp4")
 */
export const getFileExtension = (file: File): string => {
  const name = file.name.toLowerCase();
  const dotIndex = name.lastIndexOf('.');
  return dotIndex >= 0 ? name.substring(dotIndex) : '.mp4';
};
